import { themeColors } from "../../themeColors";
/**
 * @file   src\mobile\pages\ViewClientWantAd.js
 * @brief  This file is responsible for view want ads of each client section in agent dashboard
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, Tab } from "react-bootstrap";
import "../styles/clients.scss";
import PublishedWantAds from "../components/WantAdListing/PublishedWantAds";
import ClosedWantAds from "../components/WantAdListing/ClosedWantAds";
import DraftWantAds from "../components/WantAdListing/DraftWantAds";
import { PAUSED_TAB, DRAFT_TAB, PUBLISHED_TAB } from "../../constants/common";
import { getClientDetails } from "../../services/agentClientsService";
import Sortoffcanvas from "../components/common/Sortoffcanvas";
import { pageURLs } from "@/constants";
import { listContext } from "../../contextApi/wantAdListContext";
import TopHeader from "../components/common/TopHeader";
import { usePremium } from "@/hooks";
import BackLinkButton from "../components/BackLinkButton";

const ViewClientWantAd = (props) => {
  const location = useLocation();
  const clientId = location.state && location.state["clientId"];

  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const PAGE_LIMIT = 10;
  const INITIAL_PAGE_INDEX = 1;
  const initialListStates = {
    activeTab: PUBLISHED_TAB,
    currPage: INITIAL_PAGE_INDEX,
    list: null,
  };
  const [showSort, setShowSort] = useState(false);
  const sortClose = () => setShowSort(false);
  const sortShow = () => setShowSort(true);
  const [listStates, setListStates] = useState(initialListStates);
  const [clientDetails, setClientDetails] = useState(null); // to set the inventory data
  const options = [
    {
      value: "createdon",
      label: "Recently " + _.get(listStates, "activeTab", PUBLISHED_TAB),
    },
    { value: "Budget_mx", label: "Price" },
  ];
  const [sortParam, setSortParam] = useState(options[0]);

  const { shouldToggleColor } = usePremium();

  useEffect(() => {
    document.body.classList.add("bg-offcanvas-fix");
  }, []);

  useEffect(() => {
    const reqPayload = {
      page: listStates.currPage,
      limit: PAGE_LIMIT,
      status: listStates.activeTab,
      sortBy: sortParam?.value,
      clientId: clientId,
      isClientInfoReq: true,
      type: 1, // For Want-ad
    };
    getWantAdList(reqPayload);
  }, [listStates.activeTab, listStates.currPage, sortParam]);

  /**
   * To get want ad list
   * @param {*} reqPayload
   */
  const getWantAdList = async (reqPayload) => {
    await getClientDetails(reqPayload)
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const responseResult = _.get(response, "result", null);
          const currPage = reqPayload.page;
          if (currPage === INITIAL_PAGE_INDEX) {
            setListStates((prevState) => ({
              ...prevState,
              list: responseResult.wantAdList,
            }));
            setClientDetails(responseResult.clientDetails);
          } else {
            setListStates((prevState) => ({
              ...prevState,
              list: [...prevState.list, ...responseResult.wantAdList],
            }));
          }
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };
  /**
   * To handle page scroll
   */
  const handleScroll = () => {
    const el = scrollRef.current;
    const scrollHeight = el.scrollHeight;
    const scrollTop = el.scrollTop;
    const clientHeight = el.clientHeight;
    if (
      scrollHeight !== clientHeight &&
      scrollTop !== 0 &&
      Math.abs(scrollHeight - (scrollTop + clientHeight)) <= 1
    ) {
      setListStates((prevState) => ({
        ...prevState,
        currPage: prevState.currPage + 1,
      }));
    } else if (scrollTop === 0 && scrollHeight !== clientHeight) {
    }
  };

  const handleSortSelection = (selectedItem) => {
    setSortParam(selectedItem);
    sortClose();
    setListStates((prevState) => ({
      ...prevState,
      currPage: INITIAL_PAGE_INDEX,
      list: null,
    }));
  };

  const getClientLogo = (firstName, lastName) => {
    let clientName =
      (lastName ? lastName : "") + " " + (firstName ? firstName : "");
    if (clientName) {
      clientName = clientName.replace(/-/g, "").match(/\b(\w)/g);
    }
    return clientName;
  };
  return (
    <div className="gitcha-main-wrapper my-client-wrap view-client-wrap">
      <div className="content-wrapper">
        <TopHeader
          variant="threeComp"
          outerClassname="bg-white"
          topClassName="w-100 top-three-comp-headerborder"
          bottomClassName="w-100"
        >
          <BackLinkButton
            shouldToggleColor={shouldToggleColor}
            onClick={() =>
              navigate(pageURLs.viewClientDetailsMob, {
                state: { clientId: clientId },
              })
            }
          />
          <div className={shouldToggleColor ? "heading-premium" : "heading"}>
            Want-Listings
            <div className="profile-details">
              <div className="profile-img profile-bg-paleblue">
                {getClientLogo(
                  clientDetails?.FirstName,
                  clientDetails?.LastName,
                )}
              </div>
              <div className="profile-name">
                <p className="m-0">
                  {clientDetails
                    ? clientDetails?.FirstName + " " + clientDetails?.LastName
                    : ""}
                </p>
              </div>
            </div>
          </div>
          <div className="right-mobbtn-wrap">
            <a
              className="action-btn"
              href={void 0}
              role="button"
              onClick={() => sortShow()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 10C10 10.1658 9.93415 10.3247 9.81694 10.4419C9.69973 10.5592 9.54076 10.625 9.375 10.625H3.75C3.58424 10.625 3.42527 10.5592 3.30806 10.4419C3.19085 10.3247 3.125 10.1658 3.125 10C3.125 9.83424 3.19085 9.67527 3.30806 9.55806C3.42527 9.44085 3.58424 9.375 3.75 9.375H9.375C9.54076 9.375 9.69973 9.44085 9.81694 9.55806C9.93415 9.67527 10 9.83424 10 10ZM3.75 5.625H14.375C14.5408 5.625 14.6997 5.55915 14.8169 5.44194C14.9342 5.32473 15 5.16576 15 5C15 4.83424 14.9342 4.67527 14.8169 4.55806C14.6997 4.44085 14.5408 4.375 14.375 4.375H3.75C3.58424 4.375 3.42527 4.44085 3.30806 4.55806C3.19085 4.67527 3.125 4.83424 3.125 5C3.125 5.16576 3.19085 5.32473 3.30806 5.44194C3.42527 5.55915 3.58424 5.625 3.75 5.625ZM8.125 14.375H3.75C3.58424 14.375 3.42527 14.4408 3.30806 14.5581C3.19085 14.6753 3.125 14.8342 3.125 15C3.125 15.1658 3.19085 15.3247 3.30806 15.4419C3.42527 15.5592 3.58424 15.625 3.75 15.625H8.125C8.29076 15.625 8.44973 15.5592 8.56694 15.4419C8.68415 15.3247 8.75 15.1658 8.75 15C8.75 14.8342 8.68415 14.6753 8.56694 14.5581C8.44973 14.4408 8.29076 14.375 8.125 14.375ZM17.9422 12.6828C17.8841 12.6247 17.8152 12.5786 17.7393 12.5472C17.6635 12.5157 17.5821 12.4995 17.5 12.4995C17.4179 12.4995 17.3365 12.5157 17.2607 12.5472C17.1848 12.5786 17.1159 12.6247 17.0578 12.6828L15 14.7414V8.75C15 8.58424 14.9342 8.42527 14.8169 8.30806C14.6997 8.19085 14.5408 8.125 14.375 8.125C14.2092 8.125 14.0503 8.19085 13.9331 8.30806C13.8158 8.42527 13.75 8.58424 13.75 8.75V14.7414L11.6922 12.6828C11.5749 12.5655 11.4159 12.4997 11.25 12.4997C11.0841 12.4997 10.9251 12.5655 10.8078 12.6828C10.6905 12.8001 10.6247 12.9591 10.6247 13.125C10.6247 13.2909 10.6905 13.4499 10.8078 13.5672L13.9328 16.6922C13.9909 16.7503 14.0598 16.7964 14.1357 16.8279C14.2115 16.8593 14.2929 16.8755 14.375 16.8755C14.4571 16.8755 14.5385 16.8593 14.6143 16.8279C14.6902 16.7964 14.7591 16.7503 14.8172 16.6922L17.9422 13.5672C18.0003 13.5091 18.0464 13.4402 18.0779 13.3643C18.1093 13.2885 18.1255 13.2071 18.1255 13.125C18.1255 13.0429 18.1093 12.9615 18.0779 12.8857C18.0464 12.8098 18.0003 12.7409 17.9422 12.6828Z"
                  fill={
                    shouldToggleColor
                      ? themeColors.neutral000
                      : themeColors.neutral890
                  }
                />
              </svg>
            </a>
          </div>
        </TopHeader>
        <div
          className="inner-layout-content-new"
          onScroll={handleScroll}
          ref={scrollRef}
        >
          <div className="tab-wrap tab-wrap-fix client-wantad-list">
            <div className="tab-scroll-hide"></div>
            <listContext.Provider
              value={{
                list: _.get(listStates, "list", null),
                status: _.get(listStates, "activeTab", []),
                isFromAgentDashboard: true,
                clientId: clientId,
                clienFirstName: clientDetails?.FirstName,
              }}
            >
              <Tabs
                id="controlled-tab-example"
                defaultActiveKey={PUBLISHED_TAB}
                activeKey={_.get(listStates, "activeTab", PUBLISHED_TAB)}
                onSelect={(tab) => {
                  setListStates((prevState) => ({
                    ...prevState,
                    activeTab: tab,
                    currPage: INITIAL_PAGE_INDEX,
                    list: null,
                  }));
                }}
              >
                <Tab eventKey={PUBLISHED_TAB} title={PUBLISHED_TAB}>
                  <PublishedWantAds />
                </Tab>
                <Tab eventKey={PAUSED_TAB} title={PAUSED_TAB}>
                  <ClosedWantAds />
                </Tab>

                <Tab eventKey={DRAFT_TAB} title={DRAFT_TAB}>
                  <DraftWantAds />
                </Tab>
              </Tabs>
            </listContext.Provider>
          </div>
        </div>
      </div>
      <div className="inner-layout-footer-new stack-index-100">
        <div className="inner-btm-fixed-nav">
          <div className="btn-wrap">
            <button
              type="button"
              className="btn btn-primary w-100"
              onClick={() => navigate(pageURLs.mobilePremiumAgentCreateWantAd)}
            >
              Post New Want-Listing
            </button>
          </div>
        </div>
      </div>
      <Sortoffcanvas
        show={showSort}
        onHide={sortClose}
        handleSortParam={handleSortSelection}
        options={options}
        sortParam={sortParam}
      />
    </div>
  );
};

export default ViewClientWantAd;
